import { hide } from "redux-modal";
import { push } from "react-router-redux";
import { toast } from "react-toastify";
import formErrorsHandler, { formErrorLoading } from "api/global/form-errors-handler";
import defaultErrorHandler from "api/global/default-error-handler";

import {
    DRAFTS_LOADING,
    FETCH_DRAFTS_SUCCESS,
    CLEAR_DRAFTS_SUCCESS,
    FETCH_DRAFT,
    FETCH_DRAFT_SUCCESS,
    ADD_STORES_TO_DRAFT,
    DELETE_STORE_FROM_DRAFT,
    ADD_JOBS_TO_DRAFT,
    DELETE_JOB_FROM_DRAFT,
    DELETE_DATE_FROM_JOB_PRODUCT,
    DELETE_DRAFT,
    FETCH_DRAFTS_DETAILS,
    FETCH_DRAFTS_DETAILS_SUCCESS,
    SET_DRAFT_STATUS_SUCCESS,
    DRAFTS_LOADING_DISABLE,
    SET_DRAFT_DETAIL_ROW_SUCCESS,
    DRAFT_WORKER_DELETE,
    DRAFT_WORKER_INSERT,
    DRAFT_WORKER_UPDATE,
    DRAFT_FIND_MISSING_WORKERS,
    DRAFT_WORKER_BULK_REPLACE,
    FETCH_SCHEDULE_REPORT_FIELDS,
    SCHEDULE_REPORT_FIELDS_LOADING
} from "actions/types";

import {
    getDraftSchedules,
    getDraftSchedule,
    createDraftSchedule,
    addStoresToDraft,
    deleteStoreFromDraft,
    addJobsToDraft,
    deleteJobFromDraft,
    updateDraftJob,
    addDateToJobProductRequest,
    deleteDateToJobProductRequest,
    editDraftRequest,
    deleteDraftRequest,
    getDraftsDetailsPagedRequest,
    setDraftStatusRequest,
    batchBulkEditRequest,
    batchEditCellRequest,
    deleteDraftsRequest,
    inactivateDraftsRequest,
    exportDraftsRequest,
    draftSchedulerWorkerDeleteRequest,
    draftSchedulerWorkerInsertRequest,
    draftSchedulerWorkerUpdateRequest,
    draftSchedulerFindMissingWorkersRequest,
    bulkReplaceWorkerRequest,
    requestRemoveAllWorkers,
    getReportContacts,
    getReportJobChargeRate,
    getMerchandiserPayRate,
    exportScheduleReportExcel,
    getScheduleReportFields,
    exportPickPackReportExcel,
    exportChargeApprovalReportExcel,
    exportMiscChargeReportExcel,
    exportTravelLocationsReportExcel
} from "api/draftApi";
import moment from "moment";
import exportFromBlob from "../helpers/export-from-blob";

export const setDraftLoading = () => ({
    type: DRAFTS_LOADING
});

export const setDraftStatusResponse = payload => ({
    type: SET_DRAFT_STATUS_SUCCESS,
    payload
});

export const fetchDrafts = () => dispatch => {
    dispatch(setDraftLoading());
    getDraftSchedules()
        .then(response => {
            dispatch({
                type: FETCH_DRAFTS_SUCCESS,
                payload: response.data
            });
        })
        .catch((error) => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const clearDrafts = () => dispatch => {
    dispatch({
        type: CLEAR_DRAFTS_SUCCESS,
        payload: []
    });
};

export const fetchDraft = (id) => dispatch => {
    dispatch(setDraftLoading());
    getDraftSchedule(id)
        .then(response => {
            dispatch({ type: FETCH_DRAFT_SUCCESS, payload: response.data });
        })
        .catch(() => {
            dispatch(defaultErrorHandler({ message: "Could not fetch a draft schedule." }, FETCH_DRAFT));
        });
};

export const createDraft = (requestData, callback = false) => dispatch => {
        // dispatch(setDraftLoading());
        dispatch(formErrorLoading());
        createDraftSchedule(requestData)
            .then(() => {
                dispatch(formErrorLoading(false));
                dispatch(hide("newDraft"));
                dispatch(fetchDrafts());

                if (callback) {
                    callback(true);
                }
            })
            .catch(error => {
                if (callback) {
                    callback(error);
                }
            });
    };

export const addStores = (draftId, storeIds) => async dispatch => {
    dispatch(setDraftLoading());
    const failedStoreIds = [];
    await Promise.all(storeIds.map(async (id) => {
        try {
            await addStoresToDraft(draftId, id);
        } catch (error) {
            failedStoreIds.push(id);
        }
    }));
    dispatch(fetchDraft(draftId));
    if (failedStoreIds.length) {
        const description = failedStoreIds.join(", ");
        dispatch(defaultErrorHandler({
            message: `Could not add these stores to this draft. (${description})`
        }, ADD_STORES_TO_DRAFT));
    }
};

export const deleteStore = (draftId, id, storeNumber) => async dispatch => {
    dispatch(setDraftLoading());
    deleteStoreFromDraft(draftId, id)
        .then(() => {
            dispatch(fetchDraft(draftId));

            toast(`Store "${storeNumber}" has been deleted`, {
                type: toast.TYPE.SUCCESS,
            });
        })
        .catch(() => {
            dispatch(defaultErrorHandler({
                message: "Could not delete store from draft."
            }, DELETE_STORE_FROM_DRAFT));
        });
};

export const addJobs = (draftId, jobIds) => async dispatch => {
    dispatch(setDraftLoading());
    const failedJobIds = [];
    await Promise.all(jobIds.map(async (id) => {
        try {
            await addJobsToDraft(draftId, id);
        } catch (error) {
            failedJobIds.push(id);
        }
    }));
    dispatch(fetchDraft(draftId));
    if (failedJobIds.length) {
        const description = failedJobIds.join(", ");
        dispatch(defaultErrorHandler({
            message: `Could not add these jobs to this draft. (${description})`
        }, ADD_JOBS_TO_DRAFT));
    }
};

export const deleteJob = (draftId, id) => async dispatch => {
    dispatch(setDraftLoading());
    deleteJobFromDraft(draftId, id)
        .then(() => {
            dispatch(fetchDraft(draftId));
        })
        .catch(() => {
            dispatch(defaultErrorHandler({
                message: "Could not delete job from draft."
            }, DELETE_JOB_FROM_DRAFT));
        });
};

export const updateJob = (
    id,
    draftId,
    jobProductId,
    helperJobProductId,
    serviceTypeId,
    callback = false
) => async dispatch => {
    dispatch(setDraftLoading());
    updateDraftJob(id, draftId, jobProductId, helperJobProductId, serviceTypeId)
        .then(() => {
            if (callback) {
                dispatch(setDraftLoading());
                callback();
            } else {
                dispatch(fetchDraft(draftId));
            }
        })
        .catch((error) => {
            dispatch(formErrorsHandler(error));
        });
};

export const addDateToJobProduct = (id, startDate, endDate, draftId) => async dispatch => {
    dispatch(setDraftLoading());
    addDateToJobProductRequest(id, startDate, endDate)
        .then(() => {
            dispatch(fetchDraft(draftId));
        })
        .catch((error) => {
            dispatch(formErrorsHandler(error));
        });
};

export const deleteDateToJobProduct = (id, draftId) => async dispatch => {
    dispatch(setDraftLoading());
    deleteDateToJobProductRequest(id)
        .then(() => {
            dispatch(fetchDraft(draftId));
        })
        .catch(() => {
            dispatch(defaultErrorHandler({
                message: "Could not update job in draft."
            }, DELETE_DATE_FROM_JOB_PRODUCT));
        });
};

export const editDraft = (id, name, isInactive) => async dispatch => {
    dispatch(setDraftLoading());
    editDraftRequest(id, name, isInactive)
        .then(() => {
            dispatch(fetchDraft(id));
            dispatch(push("/schedule/drafts"));
        })
        .catch(error => {
            dispatch(formErrorsHandler(error));
        });
};

export const deleteDraft = (id) => async dispatch => {
    dispatch(setDraftLoading());
    deleteDraftRequest(id)
        .then(() => {
            dispatch(fetchDrafts());
            dispatch(push("/schedule/drafts"));
        })
        .catch(() => {
            dispatch(defaultErrorHandler({
                message: "Could not delete draft."
            }, DELETE_DRAFT));
        });
};

export const fetchDraftsDetailsPaged = requestParams => dispatch => {
    dispatch(setDraftLoading());
    getDraftsDetailsPagedRequest(requestParams)
        .then(response => {
            dispatch({
                type: FETCH_DRAFTS_DETAILS_SUCCESS,
                payload: response.data
            });
        })
        .catch(() => {
            dispatch(defaultErrorHandler({
                message: "Could not fetch drafts details."
            }, FETCH_DRAFTS_DETAILS));
        });
};

/**
 * Use Only for export.
 * @param {Object} requestParams
 * @param {Function} callback
 * @return {function(*)}
 */
export const fetchDraftsDetailsForExport = (requestParams, callback) => dispatch => {
    getDraftsDetailsPagedRequest(requestParams)
        .then(response => {
            if (callback) {
                callback(response.data);
            } else {
                dispatch({
                    type: FETCH_DRAFTS_DETAILS_SUCCESS,
                    payload: response.data
                });
            }
        })
        .catch(() => {
            toast("Could not fetch drafts details.", {
                type: toast.TYPE.ERROR,
                autoClose: false
            });
        });
};

/**
 * Bulk edit.
 * @param {String} field
 * @param {String|Number} value
 * @param {Object} metadata
 * @returns {Function}
 */
export const batchBulkEdit = (field, value, metadata) => dispatch => {
    dispatch(setDraftLoading());
    batchBulkEditRequest(field, value, metadata)
        .then((response) => {
            dispatch({
                type: FETCH_DRAFTS_DETAILS_SUCCESS,
                payload: response.data
            });
        })
        .catch((error) => {
            dispatch(formErrorsHandler(error));
        });
};
export const methodRemoveAllWorkers = (metadata) => dispatch => {
    dispatch(setDraftLoading());
    requestRemoveAllWorkers(metadata)
        .then((response) => {
            dispatch({
                type: FETCH_DRAFTS_DETAILS_SUCCESS,
                payload: response.data
            });
        })
        .catch((error) => {
            dispatch(formErrorsHandler(error));
        });
};


/**
 * Edit cell on draft details page.
 * @param {Number} draftDetailId -> draftDetailRowId
 * @param {String} field
 * @param {String|Number} value
 * @returns {Function}
 */
export const batchEditCell = (draftDetailId, field, value) => dispatch => {
    dispatch(setDraftLoading());
    batchEditCellRequest(draftDetailId, field, value)
        .then(() => {
            dispatch({
                type: SET_DRAFT_DETAIL_ROW_SUCCESS,
                payload: {
                    draftDetailId,
                    field,
                    value
                }
            });
        })
        .catch((error) => {
            dispatch(formErrorsHandler(error));
            dispatch({
                type: DRAFTS_LOADING_DISABLE,
                payload: false
            });
        });
};

/**
 * Set draft status.
 * @param {Number} draftId
 * @param {String} status -> [Completed, InProgress, ReadyForSupervisor, Exported]
 * @return {function(*)}
 */
export const setDraftStatus = (draftId, status) => dispatch => {
    dispatch(setDraftLoading());
    setDraftStatusRequest(draftId, status)
        .then(response => {
            dispatch(setDraftStatusResponse({
                draftId,
                data: response.data
            }));
        })
        .catch((error) => {
            dispatch(formErrorsHandler(error));
        });
};

/**
 * Delete multiple drafts.
 * @param {Array} draftIdArr
 * @param {Function|boolean} callback - success callback
 * @returns {Function}
 */
export const deleteDrafts = (draftIdArr, callback = false) => dispatch => {
    dispatch(setDraftLoading());
    deleteDraftsRequest(draftIdArr)
        .then(response => {
            toast("Selected drafts deleted successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFTS_LOADING_DISABLE,
                payload: response.data
            });

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const inactivateDrafts = (draftIdArr, callback = false) => dispatch => {
    dispatch(setDraftLoading());
    inactivateDraftsRequest(draftIdArr)
        .then(response => {
            toast("Selected drafts deactivated successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFTS_LOADING_DISABLE,
                payload: response.data
            });

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const exportDrafts = (draftIdArr, callback = false) => dispatch => {
    dispatch(setDraftLoading());
    exportDraftsRequest(draftIdArr)
        .then(response => {
            toast("Selected drafts exported successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFTS_LOADING_DISABLE,
                payload: response.data
            });

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const draftSchedulerWorkerDelete = (draftDetailRowId, workerRowId) => dispatch => {
    dispatch(setDraftLoading());
    draftSchedulerWorkerDeleteRequest(draftDetailRowId, workerRowId)
        .then(() => {
            toast("Worker deleted successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFT_WORKER_DELETE,
                payload: {
                    draftDetailRowId,
                    workerRowId
                }
            });
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const draftSchedulerWorkerInsert = (draftDetailRowId, workerId, isTeamLead) => dispatch => {
    dispatch(setDraftLoading());
    draftSchedulerWorkerInsertRequest(draftDetailRowId, workerId, isTeamLead)
        .then((response) => {
            toast("Worker added successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFT_WORKER_INSERT,
                payload: {
                    draftDetailRowId,
                    data: response.data
                }
            });
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const draftSchedulerWorkerUpdate = (
    draftDetailRowId,
    workerRowId,
    workerId,
    isTemporary
) => dispatch => {
    dispatch(setDraftLoading());
    draftSchedulerWorkerUpdateRequest(draftDetailRowId, workerRowId, workerId, isTemporary)
        .then((response) => {
            toast("Worker changed successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFT_WORKER_UPDATE,
                payload: {
                    draftDetailRowId,
                    workerRowId,
                    data: response.data
                }
            });
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const draftSchedulerFindMissingWorkers =
    (jobId, metadata, helperJobId = null, numberOfHelpers = null, callback = false) => dispatch => {
    dispatch(setDraftLoading());
    draftSchedulerFindMissingWorkersRequest(jobId, metadata, helperJobId, numberOfHelpers)
        .then((response) => {
            dispatch({
                type: DRAFT_FIND_MISSING_WORKERS,
                payload: response.data
            });

            if (callback) {
                callback();
            }
        })
        .catch(error => {
            dispatch({
                type: DRAFTS_LOADING_DISABLE,
                payload: false
            });
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};


export const bulkReplaceWorkerMethod = (oldWorkerId, newWorkerId, metadata) => dispatch => {
    // console.error("oldWorkerId, newWorkerId, metadata", oldWorkerId, newWorkerId, metadata);
    dispatch(setDraftLoading());
    bulkReplaceWorkerRequest(oldWorkerId, newWorkerId, metadata)
        .then((response) => {
            toast("Worker replaced successfully", {
                type: toast.TYPE.SUCCESS,
            });

            dispatch({
                type: DRAFT_WORKER_BULK_REPLACE,
                payload: response.data
            });
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestGetReportContacts = (callback) => dispatch => {
    getReportContacts()
        .then((response) => {
            callback(response.data);
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};


export const requestGetReportJobChargeRate = (callback) => dispatch => {
    getReportJobChargeRate()
        .then((response) => {
            callback(response.data);
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestMerchandiserPayRate = (callback) => dispatch => {
    getMerchandiserPayRate()
        .then((response) => {
            callback(response.data);
        })
        .catch(error => {
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestGetScheduleReportsLoading = (loading) => dispatch => {
    dispatch({
        type: SCHEDULE_REPORT_FIELDS_LOADING,
        payload: loading
    });
};

export const requestGetScheduleReportFields = () => dispatch => {
    dispatch(requestGetScheduleReportsLoading(true));
    getScheduleReportFields()
        .then((response) => {
            dispatch({
                type: FETCH_SCHEDULE_REPORT_FIELDS,
                payload: response.data
            });
        })
        .catch(error => {
            dispatch(requestGetScheduleReportsLoading(false));
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestExportScheduleReportExcel = (data, callback) => dispatch => {
    dispatch(requestGetScheduleReportsLoading(true));
    exportScheduleReportExcel(data)
        .then((response) => {
            dispatch(requestGetScheduleReportsLoading(false));
            toast("Schedule report exported successfully", {
                type: toast.TYPE.SUCCESS,
            });
            const fileName = decodeURI(response.headers["content-disposition"].split("''")[1]) ||
                `Schedule_export_${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`;
            exportFromBlob(response.data, fileName);
            callback();
        })
        .catch(error => {
            dispatch(requestGetScheduleReportsLoading(false));
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestExportPickPackReportExcel = (data, callback) => dispatch => {
    dispatch(requestGetScheduleReportsLoading(true));
    exportPickPackReportExcel(data)
        .then((response) => {
            dispatch(requestGetScheduleReportsLoading(false));
            toast("PickPack report exported successfully", {
                type: toast.TYPE.SUCCESS,
            });
            const fileName = decodeURI(response.headers["content-disposition"].split("''")[1]) ||
                `Pick_pack_export_${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`;
            exportFromBlob(response.data, fileName);
            callback();
        })
        .catch(error => {
            dispatch(requestGetScheduleReportsLoading(false));
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestExportChargeApprovalReportExcel = (data, callback) => dispatch => {
    dispatch(requestGetScheduleReportsLoading(true));
    exportChargeApprovalReportExcel(data)
        .then((response) => {
            dispatch(requestGetScheduleReportsLoading(false));
            toast("Charge Approval report exported successfully", {
                type: toast.TYPE.SUCCESS,
            });
            const fileName = decodeURI(response.headers["content-disposition"].split("''")[1]) ||
                `Charge_approval_export_${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`;
            exportFromBlob(response.data, fileName);
            callback();
        })
        .catch(error => {
            dispatch(requestGetScheduleReportsLoading(false));
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestExportMiscChargeReportExcel = (data, callback) => dispatch => {
    dispatch(requestGetScheduleReportsLoading(true));
    exportMiscChargeReportExcel(data)
        .then((response) => {
            dispatch(requestGetScheduleReportsLoading(false));
            toast("Misc charge report exported successfully", {
                type: toast.TYPE.SUCCESS,
            });
            const fileName = decodeURI(response.headers["content-disposition"].split("''")[1]) ||
                `MiscCharge_export_${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`;
            exportFromBlob(response.data, fileName);
            callback();
        })
        .catch(error => {
            dispatch(requestGetScheduleReportsLoading(false));
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};

export const requestExportTravelLocationsReportExcel = (data) => dispatch => {
    dispatch(requestGetScheduleReportsLoading(true));
    exportTravelLocationsReportExcel(data)
        .then((response) => {
            dispatch(requestGetScheduleReportsLoading(false));
            toast("Travel location report exported successfully", {
                type: toast.TYPE.SUCCESS,
            });
            const fileName = decodeURI(response.headers["content-disposition"].split("''")[1]) ||
                `TravelLocation_export_${moment().format("YYYY-MM-DD-HH-mm")}.xlsx`;
            exportFromBlob(response.data, fileName);
        })
        .catch(error => {
            dispatch(requestGetScheduleReportsLoading(false));
            dispatch(defaultErrorHandler(error, FETCH_DRAFT));
        });
};
